import { Modal } from 'antd';
import { useAppSelector, useAppTranslation } from 'app/hooks';
import CloseIcon from 'assets/icons/CloseIcon';
import useThemes from 'hooks/useThemes';
import React, { useEffect, useState } from 'react';
import { Connector, useAccount, useConnect, useDisconnect } from 'wagmi';
import walletconnect from 'assets/icons/walletconnect_icon.svg';
import metamask from 'assets/icons/metamask_icon.svg';
import trustwallet from 'assets/icons/trustwallet_icon.svg';
import vinpay from 'assets/images/deposit-methods/vinpay.png';
import Typo from 'consts/typography';
import VinpayConnectModal from './Modals/VinpayConnectModal';
import { selectCurrentUser } from 'redux/userSlice';
import vinpayApi from 'api/vinpayApi';
import useUserDetail from 'hooks/useUserDetail';
import { useHandleResponseError } from 'hooks/useHandleResponseError';
import { shortenAddress } from 'utils/commonUtils';

interface WalletConnectProps {}

interface WalletModel {
  id: string;
  logo: string;
}

const wallets: WalletModel[] = [
  {
    id: 'walletConnect',
    logo: walletconnect,
  },
  {
    id: 'io.metamask',
    logo: metamask,
  },
  {
    id: 'com.trustwallet.app',
    logo: trustwallet,
  },
];

const WalletConnect: React.FunctionComponent<WalletConnectProps> = () => {
  const { currentTheme } = useThemes();
  const t = useAppTranslation();
  const { address, connector } = useAccount();
  const { connectors, connect, isSuccess, isPending, isError } = useConnect();
  const { disconnect } = useDisconnect();
  const currentUser = useAppSelector(selectCurrentUser);
  const { getUser, isUserLoggedIn } = useUserDetail();
  const handleResponseError = useHandleResponseError();
  const [isOpenVinpayConnectModal, setIsOpenVinpayConnectModal] = useState<boolean>(false);

  const handleConnectWallet = (connector?: Connector) => {
    if (!connector) return;
    connect({ connector });
  };

  const handleVinpayConnectClick = () => {
    setIsOpenVinpayConnectModal(!isOpenVinpayConnectModal);
  };

  const renderWallet = (wallet: WalletModel) => {
    return (
      <div
        key={`connect-wallet-${wallet.id}`}
        className="flex items-center gap-3 w-full max-width-300"
      >
        <div className="flex items-center gap-3 min-width-160">
          <img src={wallet.logo} width={40} height={40} alt="payment method" />
          <div>
            <span className="primary-text-color font-bold">
              {connectors.find((x) => x.id === wallet.id)?.name}
            </span>
            <br />
            {isUserLoggedIn && address && connector?.id === wallet.id && (
              <span className="row-infor__right span">{shortenAddress(address)}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 w-full">
          {isUserLoggedIn && (
            <button
              disabled={connector && connector?.id !== wallet.id}
              className={`button-generate ${address && 'btn-danger'} ${address && connector?.id !== wallet.id && 'disabled'}`}
              type="submit"
              onClick={() => {
                handleConnectWallet(connectors.find((x) => x.id === wallet.id));
              }}
            >
              {!address || connector?.id !== wallet.id
                ? t('common.connect')
                : t('common.disconnect')}
            </button>
          )}
        </div>
      </div>
    );
  };

  const handleDisconnectVinpay = async () => {
    const { ok, error } = await vinpayApi.disconnect();
    if (ok) {
      getUser();
    }
    if (error) {
      handleResponseError(error);
    }
  };

  useEffect(() => {
    if (isError) {
      disconnect();
    }
  }, [isError]);

  return (
    <div className="payment-methods primary-text-color items-center-important">
      {isPending || isOpenVinpayConnectModal ? (
        <div className="text-center w-100">
          <p className={`font-bold ${Typo.p2}`}>{t('common.connecting')}...</p>
        </div>
      ) : (
        <>
          {wallets.filter((x) => connectors.map((con) => con.id).includes(x.id)).map(renderWallet)}

          {/* Vinpay */}
          <div className="flex justify-center items-center gap-3 w-full max-width-300">
            <div className="flex items-center gap-3 min-width-160">
              <img src={vinpay} width={40} height={40} alt="payment method" />
              <span className="primary-text-color font-bold">Vinpay</span>
            </div>
            <div className="flex items-center gap-3 w-full">
              {isUserLoggedIn && (
                <button
                  className={`button-generate ${currentUser?.isVinpayConnected && 'btn-danger'}`}
                  type="submit"
                  onClick={
                    currentUser?.isVinpayConnected
                      ? handleDisconnectVinpay
                      : handleVinpayConnectClick
                  }
                >
                  {!currentUser?.isVinpayConnected ? t('common.connect') : t('common.disconnect')}
                </button>
              )}
            </div>
          </div>
        </>
      )}
      <VinpayConnectModal
        visible={isOpenVinpayConnectModal}
        closeModal={handleVinpayConnectClick}
        className="common-modal withdraw-modal"
      />
    </div>
  );
};

export default WalletConnect;
